import { useCallback, useEffect, useRef } from 'react';

interface ModalClipboardPasteOptions {
  onImagesPasted: (files: File[]) => void;
  onError?: (error: string) => void;
  maxImages?: number;
  disabled?: boolean;
  enablePaste?: boolean;
}

export const useModalClipboardPaste = (options: ModalClipboardPasteOptions) => {
  const {
    onImagesPasted,
    onError,
    maxImages = 5,
    disabled = false,
    enablePaste = true
  } = options;

  const modalRef = useRef<HTMLDivElement>(null);

  const handlePaste = useCallback((event: ClipboardEvent) => {
    if (disabled || !enablePaste) return;

    const clipboardData = event.clipboardData;
    if (!clipboardData) return;

    const items = Array.from(clipboardData.items);
    const imageItems = items.filter(item => item.type.startsWith('image/'));

    if (imageItems.length === 0) return;

    // Prevent default paste behavior for images
    event.preventDefault();
    event.stopPropagation();

    if (imageItems.length > maxImages) {
      onError?.(`Maximum ${maxImages} images allowed. Found ${imageItems.length} images.`);
      return;
    }

    const files: File[] = [];
    let processedCount = 0;

    imageItems.forEach((item, index) => {
      const blob = item.getAsFile();
      if (!blob) {
        processedCount++;
        if (processedCount === imageItems.length && files.length > 0) {
          onImagesPasted(files);
        }
        return;
      }

      // Create a proper File object with a meaningful name
      const fileExtension = blob.type.split('/')[1] || 'png';
      const fileName = `pasted-image-${Date.now()}-${index + 1}.${fileExtension}`;
      
      const file = new File([blob], fileName, {
        type: blob.type,
        lastModified: Date.now()
      });

      files.push(file);
      processedCount++;

      if (processedCount === imageItems.length && files.length > 0) {
        onImagesPasted(files);
      }
    });

    // Handle case where no valid files were found
    if (imageItems.length > 0 && files.length === 0) {
      onError?.('No valid image files found in clipboard');
    }
  }, [disabled, enablePaste, maxImages, onImagesPasted, onError]);

  useEffect(() => {
    const modalElement = modalRef.current;
    if (!modalElement || !enablePaste) return;

    // Add paste listener to the modal container
    modalElement.addEventListener('paste', handlePaste);

    return () => {
      modalElement.removeEventListener('paste', handlePaste);
    };
  }, [handlePaste, enablePaste]);

  return {
    modalRef,
    handlePaste
  };
};

export default useModalClipboardPaste;

// Export statement to make this a proper module
export {};